import React, { useEffect, useRef } from "react";
import "./ChatBubble.css";
import { applyBubbleHtml } from "./chatBubbleDom.js";

function extractActionPayload(dataset = {}, actionKey) {
  const payload = { ...dataset };
  delete payload[actionKey];
  return payload;
}

function ChatBubble({
  bubbleId,
  messageId,
  role = "assistant",
  html = "",
  bubbleStyle = null,
  streamingManager,
  chatAreaMethods,
  onContextMenu,
  onToolUiAction,
  onBubbleAction,
  ...bubbleData
}) {
  const rootRef = useRef(null);

  useEffect(() => {
    if (!streamingManager || !rootRef.current || !bubbleId) {
      return undefined;
    }

    streamingManager.registerElement(bubbleId, "rootElement", rootRef.current);
    return () => {
      streamingManager.unregisterElement(bubbleId, "rootElement");
    };
  }, [streamingManager, bubbleId]);

  useEffect(() => {
    if (!rootRef.current) {
      return;
    }

    applyBubbleHtml(rootRef.current, html);
  }, [html]);

  const bubbleContext = {
    bubbleId,
    messageId,
    bubble: { bubbleId, messageId, role, html, onToolUiAction, onBubbleAction, ...bubbleData },
    chatAreaMethods,
    updateBubble: chatAreaMethods?.updateBubble,
  };

  const handleClick = (event) => {
    const bubbleActionEl = event.target?.closest?.("[data-bubble-action]");
    if (bubbleActionEl && rootRef.current?.contains(bubbleActionEl)) {
      event.stopPropagation();
      const actionResult = onBubbleAction?.(
        bubbleActionEl.dataset.bubbleAction,
        extractActionPayload(bubbleActionEl.dataset, "bubbleAction"),
        bubbleContext
      );
      if (actionResult) {
        chatAreaMethods?.handleBubbleClickAction?.(actionResult);
      }
      return;
    }

    const toolActionEl = event.target?.closest?.("[data-tool-action]");
    if (toolActionEl && rootRef.current?.contains(toolActionEl)) {
      event.stopPropagation();
      const actionResult = onToolUiAction?.(
        toolActionEl.dataset.toolAction,
        extractActionPayload(toolActionEl.dataset, "toolAction"),
        bubbleContext
      );
      if (actionResult) {
        chatAreaMethods?.handleBubbleClickAction?.(actionResult);
      }
    }
  };

  const handleContextMenu = (event) => {
    if (!onContextMenu || !rootRef.current) {
      return;
    }

    const explicitContextTarget = event.target?.closest?.(
      "[data-bubble-context-menu-target]"
    );
    const hasExplicitTargets = rootRef.current.querySelector(
      "[data-bubble-context-menu-target]"
    );

    if (hasExplicitTargets && !explicitContextTarget) {
      return;
    }

    onContextMenu(event);
  };

  return (
    <div
      ref={rootRef}
      className={[
        "chat-bubble",
        `chat-bubble-${role === "user" ? "user" : "assistant"}`,
        bubbleStyle === "bubble"
          ? "chat-bubble-bubble chat-bubble-function-call chat-bubble-function-call-bubble"
          : bubbleStyle === "simple"
            ? "chat-bubble-function-call chat-bubble-function-call-simple"
            : "",
      ]
        .filter(Boolean)
        .join(" ")}
      data-bubble-id={bubbleId}
      data-message-id={messageId}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
    />
  );
}

export const ChatBubblePresets = {};

export default ChatBubble;
